import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
  Switch,
  FormControlLabel,
  Divider,
  Link as MuiLink,
} from '@mui/material';
import { fetchTrip, updateTrip, suggestTripSlug, isTripSlugAvailable, isValidTripSlug } from '../../lib/trips';
import { fetchMyProfile } from '../../lib/profile';
import { Trip, UserProfile } from '../../types';

// Trip metadata editor. Cover-photo picker + rich-text description come next;
// this page focuses on the publish-critical fields first.
export default function TripEditPage() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [isPublished, setIsPublished] = useState(false);
  const [isPublic, setIsPublic] = useState(false);

  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!tripId) return;
    (async () => {
      const [t, p] = await Promise.all([fetchTrip(tripId), fetchMyProfile()]);
      if (t.error) setSaveError(t.error);
      if (t.data) {
        setTrip(t.data);
        setTitle(t.data.title);
        setDescription(t.data.description ?? '');
        setSlug(t.data.slug ?? suggestTripSlug(t.data.title));
        setIsPublished(t.data.is_published);
        setIsPublic(t.data.is_public);
      }
      if (p.data) setProfile(p.data);
      setLoading(false);
    })();
  }, [tripId]);

  // Debounced slug uniqueness check
  useEffect(() => {
    if (!trip || !slug) return;
    if (slug === trip.slug) {
      setSlugStatus('idle');
      return;
    }
    if (!isValidTripSlug(slug)) {
      setSlugStatus('invalid');
      return;
    }
    setSlugStatus('checking');
    const timer = setTimeout(() => {
      isTripSlugAvailable(slug, trip.trip_id).then(({ available }) => {
        setSlugStatus(available ? 'available' : 'taken');
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [slug, trip]);

  async function handleSave() {
    if (!trip) return;
    setSaveError(null);
    setSaveMessage(null);
    if (!isValidTripSlug(slug)) {
      setSaveError('Slug must be lowercase letters, digits, and hyphens; 2–80 chars.');
      return;
    }
    if (slugStatus === 'taken') {
      setSaveError('That slug is already used by another of your trips.');
      return;
    }
    // Enforce publish invariant client-side too
    if (isPublic && !isPublished) {
      setSaveError('A public trip must also be published.');
      return;
    }
    setSaving(true);
    const { data, error } = await updateTrip(trip.trip_id, {
      title: title.trim(),
      description: description.trim() || null,
      slug,
      is_published: isPublished,
      is_public: isPublic,
    });
    setSaving(false);
    if (error) {
      setSaveError(error);
      return;
    }
    if (data) {
      setTrip(data);
      setSaveMessage('Saved.');
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }
  if (!trip) return <Typography color="text.secondary">Trip not found.</Typography>;

  const userSlug = profile?.slug ?? '<your-slug>';
  const publicUrl = `hiyouji.com/${userSlug}/${slug || '<slug>'}`;

  const slugHelper =
    slugStatus === 'invalid'
      ? 'Invalid — lowercase letters, digits, hyphens (2–80 chars).'
      : slugStatus === 'checking'
      ? 'Checking availability…'
      : slugStatus === 'available'
      ? '✓ Available'
      : slugStatus === 'taken'
      ? '✗ Already used by another of your trips'
      : `Public URL: ${publicUrl}`;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <MuiLink component="button" onClick={() => navigate(`/trips/${trip.trip_id}`)} sx={{ display: 'block', mb: 2 }}>
        ← Back to trip
      </MuiLink>
      <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>
        Edit trip
      </Typography>

      <Stack spacing={3}>
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          helperText="Shown on the trip cover and the URL suggestion."
        />

        <TextField
          label="Slug"
          value={slug}
          onChange={(e) => setSlug(suggestTripSlug(e.target.value))}
          fullWidth
          helperText={slugHelper}
          error={slugStatus === 'invalid' || slugStatus === 'taken'}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">{`hiyouji.com/${userSlug}/`}</InputAdornment>,
            },
          }}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          helperText="A short intro shown at the top of the trip page. Optional."
        />

        <Divider>Publishing</Divider>

        <FormControlLabel
          control={
            <Switch
              checked={isPublished}
              onChange={(e) => {
                const next = e.target.checked;
                setIsPublished(next);
                // Turning off publish also turns off public — the DB enforces this.
                if (!next) setIsPublic(false);
              }}
            />
          }
          label={
            <Box>
              <Typography>Published</Typography>
              <Typography variant="caption" color="text.secondary">
                Anyone with the link can view. Not listed on your profile unless "public" is also on.
              </Typography>
            </Box>
          }
        />

        <FormControlLabel
          control={
            <Switch
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              disabled={!isPublished}
            />
          }
          label={
            <Box>
              <Typography>Public (listed on profile)</Typography>
              <Typography variant="caption" color="text.secondary">
                {isPublished ? `Will appear on hiyouji.com/${userSlug}.` : 'Publish first to enable.'}
              </Typography>
            </Box>
          }
        />

        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {saveError ? <Alert severity="error">{saveError}</Alert> : null}

        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || slugStatus === 'checking' || slugStatus === 'taken' || slugStatus === 'invalid' || !slug || !title.trim()}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
